'use client';

import { useEffect, type RefObject } from 'react';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Makes the film stage and the wall below it two places rather than one long
 * scroll: one gesture anywhere in the stage lands you on the wall, and one
 * gesture back lands you on the film. There is no resting point in between.
 *
 * The range is the stage's own height — `top top` to `bottom top` — so
 * progress 1 is exactly the scroll position where the wall's first row sits
 * at the top of the viewport. That equivalence is the whole design, and it
 * is why this does NOT pin. Pinning inserts its own scroll distance, which
 * puts progress 1 partway through the handoff with the wall still below the
 * fold; snapping there would park the reader back on a dimmed film and make
 * them scroll again.
 *
 * Snapping is scoped to this trigger rather than done with page-wide CSS
 * `scroll-snap-type`, which would have fought every scroll through the
 * 6,600px of wall underneath.
 */
export const useStageHandoff = ({
  stage,
  film,
  enabled = true,
}: {
  stage: RefObject<HTMLElement | null>;
  film: RefObject<HTMLElement | null>;
  enabled?: boolean;
}) => {
  useEffect(() => {
    const stageEl = stage.current;
    const filmEl = film.current;
    if (!enabled || !stageEl || !filmEl) return;

    /* globals.css sets `scroll-behavior: smooth` on html and body for anchor
       links. ScrollTrigger snaps by writing the scroll position every frame,
       and native smooth scrolling intercepts each of those writes and
       animates toward it instead of applying it — the two interpolators
       fight and the snap never lands. GSAP has to own the scroll position
       while the stage is mounted. Inline styles win over the stylesheet rule
       (it carries no `!important`) and are restored on the way out, so the
       rest of the site keeps its smooth anchors. */
    const targets = [document.documentElement, document.body];
    const previous = targets.map((el) => el.style.scrollBehavior);
    targets.forEach((el) => {
      el.style.scrollBehavior = 'auto';
    });

    // Everything is scoped to a context so a single revert() tears down the
    // trigger and every tween it created. Without this, a hot reload or a
    // locale change leaves an orphaned trigger behind.
    const ctx = gsap.context(() => {
      gsap
        .timeline({
          scrollTrigger: {
            trigger: stageEl,
            start: 'top top',
            end: 'bottom top',
            // `scrub: 0.6` lets the film lag the scrollbar by just over half
            // a second, which reads as weight rather than lag.
            scrub: 0.6,
            invalidateOnRefresh: true,
            snap: {
              snapTo: [0, 1],
              // The crux of it: snap the way the reader is already going.
              // Nearest-point snapping would drag someone who has just
              // started downward back up onto the film.
              directional: true,
              duration: { min: 0.2, max: 0.5 },
              delay: 0.05,
              ease: 'power2.inOut',
            },
          },
        })
        // Transform and opacity only — both composited, so the handoff costs
        // the scroll nothing on the main thread. Modest on purpose: the film
        // is drawing back as the wall arrives, not blacking out.
        .to(filmEl, { scale: 0.94, opacity: 0.55, ease: 'none' });
    }, stageEl);

    return () => {
      ctx.revert();
      targets.forEach((el, i) => {
        el.style.scrollBehavior = previous[i];
      });
    };
  }, [stage, film, enabled]);
};
